import { Injectable } from '@angular/core';
import { FormArray, FormGroup, AbstractControl } from '@angular/forms';
import { TipologiaCassaCode } from './sts-models';

/** STS private-invoice totals (Parcella Privati only; no SDI withholding). */
export interface StsInvoiceTotals {
  imponibile: number;
  imponibileNettoBollo: number;
  cassaTaxableBase: number;
  totaleCassaPrevidenza: number;
  imposta: number;
  bollo: number;
  totale: number;
}

/** Invoice total calculations for the STS private-expense flow only. */
@Injectable({ providedIn: 'root' })
export class StsInvoiceCalculationsService {
  isBolloLine(line: FormGroup): boolean {
    if (line.get('is_bollo')?.value === true) {
      return true;
    }
    const descrizione =
      line.get('descrizione')?.value ?? line.getRawValue()?.descrizione ?? '';
    return String(descrizione).startsWith('Imposta di Bollo');
  }

  getLineTotalValue(line: FormGroup): number {
    const raw = line.getRawValue()?.totale ?? line.get('totale')?.value;
    const parsed = parseFloat(raw ?? '');
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    const quantita = parseFloat(line.get('quantita')?.value || '0');
    const prezzo = parseFloat(line.get('prezzo')?.value || '0');
    return quantita * prezzo;
  }

  calculateInvoiceTotals(
    lines: FormGroup[],
    cassaPrevidenzaEnabled: boolean,
    percentualeCassa: number,
    regimeFiscale: string | undefined,
    escludi_bollo: boolean,
    applica_bollo: boolean,
    tipologiaCassa?: string | null
  ): StsInvoiceTotals {
    const bolloLine = lines.find((line) => this.isBolloLine(line));

    const imponibile = lines.reduce((sum, line) => {
      if (this.isBolloLine(line) && regimeFiscale && regimeFiscale !== 'RF19') {
        return sum;
      }
      return sum + this.getLineTotalValue(line);
    }, 0);

    const bollo = bolloLine ? this.getLineTotalValue(bolloLine) : 0;
    let imponibileNettoBollo = imponibile;

    if (regimeFiscale === 'RF19' && !escludi_bollo && applica_bollo) {
      imponibileNettoBollo = imponibile - 2;
    }

    // ENPAP (TC21) applies the contribution on taxable amount + stamp duty.
    const cassaTaxableBase =
      tipologiaCassa === TipologiaCassaCode.TC21
        ? imponibileNettoBollo + bollo
        : imponibileNettoBollo;

    const totaleCassaPrevidenza = cassaPrevidenzaEnabled
      ? (cassaTaxableBase * percentualeCassa) / 100
      : 0;

    const totale = imponibileNettoBollo + totaleCassaPrevidenza + bollo;

    return {
      imponibile,
      imponibileNettoBollo,
      cassaTaxableBase,
      totaleCassaPrevidenza,
      imposta: 0,
      bollo,
      totale,
    };
  }

  /**
   * Total VAT for the document: sum of VAT on each non-bollo line
   * (line total × VAT rate from `iva_option`) plus VAT on cassa previdenza
   * (rate × cassa taxable base × percentuale_cassa / 100²).
   * Added on top of `calculateInvoiceTotals().totale` as the `imposta` field.
   */
  calculateImposta(
    lines: FormGroup[],
    cassaPrevidenzaEnabled: boolean,
    cassaPrevidenzaCodiceIva: string | null,
    percentualeCassa: number,
    cassaTaxableBase: number
  ): number {
    let imposta = lines.reduce((sum, line) => {
      if (this.isBolloLine(line)) {
        return sum;
      }

      const totale = this.getLineTotalValue(line);
      const ivaOption = line.get('iva_option')?.value || '0%';
      const ivaPercentage = parseFloat(ivaOption.split('%')[0]) || 0;

      return sum + (totale * ivaPercentage) / 100;
    }, 0);

    if (cassaPrevidenzaEnabled && cassaPrevidenzaCodiceIva) {
      imposta +=
        (parseFloat(cassaPrevidenzaCodiceIva.split('%')[0]) *
          cassaTaxableBase *
          percentualeCassa) /
        100 /
        100;
    }

    return imposta;
  }

  updateFormWithCalculatedTotals(
    documentLinesArray: FormArray,
    invoiceForm: FormGroup,
    regimeFiscale: string | undefined
  ): void {
    const cassaPrevidenzaEnabled = invoiceForm.get('cassa_previdenza')?.value;
    const percentualeCassa = parseFloat(
      invoiceForm.get('percentuale_cassa')?.value || '0'
    );
    const escludi_bollo = invoiceForm.get('escludi_bollo')?.value;
    const applica_bollo = invoiceForm.get('applica_bollo')?.value;
    const tipologiaCassa = invoiceForm.get('tipologia_cassa')?.value;
    const totals = this.calculateInvoiceTotals(
      documentLinesArray.controls as FormGroup[],
      cassaPrevidenzaEnabled,
      percentualeCassa,
      regimeFiscale,
      escludi_bollo,
      applica_bollo,
      tipologiaCassa
    );

    if (!cassaPrevidenzaEnabled) {
      invoiceForm.patchValue(
        { totale_cassa_previdenza: '0.00' },
        { emitEvent: false }
      );
    }

    const cassaPrevidenzaCodiceIva = invoiceForm.get(
      'cassa_previdenza_codice_iva'
    )?.value;
    const imposta = this.calculateImposta(
      documentLinesArray.controls as FormGroup[],
      cassaPrevidenzaEnabled,
      cassaPrevidenzaCodiceIva,
      percentualeCassa,
      totals.cassaTaxableBase
    );

    invoiceForm.patchValue(
      {
        imponibile: totals.imponibile.toFixed(2),
        totale_cassa_previdenza: totals.totaleCassaPrevidenza.toFixed(2),
        imposta: imposta.toFixed(2),
        bollo: totals.bollo.toFixed(2),
        totale: (totals.totale + imposta).toFixed(2),
        totale_ritenuta: '0.00',
      },
      { emitEvent: false }
    );
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control: AbstractControl) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((c) => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          } else {
            c.markAsTouched();
          }
        });
      }
    });
  }
}
